import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaImage, FaEye, FaPlus, FaClipboardList, 
  FaGripVertical, FaTrash, FaUpload, FaLink, FaCloud,
  FaFolder, FaExclamationTriangle, FaSpinner, FaCheck, FaCheckCircle,
  FaCloudUploadAlt
} from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { 
  sanitizeFolderName,
  uploadMultipleToCloudinary
} from '../../../Functions/ImageUrlHandle';

const ArrayFieldContainer = styled.div`
  margin-bottom: 20px;
  background: ${props => props.darkMode ? '#2d2d2d' : 'white'};
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,${props => props.darkMode ? '0.2' : '0.04'});
  border: ${props => props.darkMode ? '1px solid #444' : 'none'};
`;

const ArrayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ArrayLabel = styled.label`
  font-weight: 600;
  color: ${props => props.darkMode ? '#f5f5f5' : '#2d3748'};
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: ${props => props.darkMode ? '#f5f5f5' : '#2d3748'};
  }
`;

const ArrayItemContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  background: ${props => props.darkMode ? '#1e1e1e' : '#f8f9fa'};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${props => props.darkMode ? '#444' : '#e9ecef'};
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  
  /* Ensure proper wrapping on small screens */
  @media (max-width: 576px) {
    flex-wrap: wrap;
  }
`;

const DragHandle = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: #a0aec0;
  transition: color 0.2s ease;

  &:hover {
    color: #4a5568;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ArrayItemInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.darkMode ? '#555' : '#e2e8f0'};
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  background: ${props => props.darkMode ? '#333' : 'white'};
  color: ${props => props.darkMode ? '#f5f5f5' : 'inherit'};
  min-width: 0; /* Prevent input from expanding beyond container */

  &:focus {
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
    outline: none;
  }

  &::placeholder {
    color: ${props => props.darkMode ? '#aaa' : '#a0aec0'};
  }
  
  /* Adjust width on smaller screens */
  @media (max-width: 576px) {
    width: 100%;
    margin-bottom: 8px;
  }
`;

// Add a handler for preventing form submission on input Enter
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
  }
};

const AddButton = styled(motion.button)`
  background: ${props => props.darkMode ? '#1a365d' : '#ebf8ff'};
  color: ${props => props.darkMode ? '#90cdf4' : '#3498db'};
  border: 1px dashed ${props => props.darkMode ? '#4299e1' : '#90cdf4'};
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.darkMode ? '#2a4365' : '#bee3f8'};
    border-color: ${props => props.darkMode ? '#63b3ed' : '#3498db'};
  }
`;

const DropzoneContainer = styled.div`
  border: 2px dashed ${props => props.isDragActive 
    ? '#3498db' 
    : props.darkMode ? '#555' : '#ced4da'};
  border-radius: 10px;
  padding: 30px 20px;
  text-align: center;
  background: ${props => props.isDragActive 
    ? 'rgba(52, 152, 219, 0.1)' 
    : props.darkMode ? '#1e1e1e' : '#f8f9fa'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 20px;

  &:hover {
    border-color: #3498db;
    background: ${props => props.darkMode 
      ? 'rgba(52, 152, 219, 0.15)' 
      : 'rgba(52, 152, 219, 0.05)'};
  }
`;

const DropzoneIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.isDragActive ? '#3498db' : '#6c757d'};
  margin-bottom: 10px;
`;

const DropzoneText = styled.p`
  margin: 0;
  color: ${props => props.isDragActive ? '#3498db' : '#6c757d'};
`;

const ImagePreviewsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 15px;
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 8px;
  }
  
  @media (max-width: 400px) {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 5px;
  }
`;

const ImagePreview = styled(motion.div)`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  aspect-ratio: 4/3;
  background: #f1f1f1;
  
  /* Ensure proper sizing on mobile */
  @media (max-width: 576px) {
    border-radius: 6px;
  }
`;

const SmallImagePreview = styled(motion.div)`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  aspect-ratio: 4/3;
  width: 200px; /* Constrained width */
  margin-bottom: 10px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageActions = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  overflow: hidden; /* Prevent buttons from overflowing */

  ${ImagePreview}:hover & {
    opacity: 1;
  }
  
  /* Make buttons always visible and properly positioned on mobile */
  @media (max-width: 576px) {
    opacity: 1;
    background: rgba(0,0,0,0.3);
    justify-content: flex-end;
    align-items: flex-start;
    padding: 8px;
  }
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  color: ${props => props.danger ? '#e74c3c' : props.color || '#3498db'};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background: ${props => props.danger ? '#fee2e2' : props.hoverBg || '#e2f5fe'};
  }
  
  /* Make buttons more touch-friendly on mobile */
  @media (max-width: 576px) {
    width: 28px;
    height: 28px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  }
`;

const TabHeaderButtons = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    width: 100%;
    
    button {
      width: 100%;
    }
  }
`;

const CloudinaryUploadContainer = styled.div`
  background: ${props => props.darkMode ? '#1e1e1e' : '#f8f9fa'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid ${props => props.darkMode ? '#444' : '#e9ecef'};
`;

const UploadPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  max-height: ${props => props.isExpanded ? 'none' : '120px'};
  overflow: ${props => props.isExpanded ? 'visible' : 'hidden'};
  position: relative;
`;

const PreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid ${props => props.darkMode ? '#444' : '#dee2e6'};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  span {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 3px;
    background: rgba(0,0,0,0.6);
    color: white;
    font-size: 9px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
`;

const RequiredMark = styled.span`
  color: #e74c3c;
  margin-left: 3px;
`;

const UploadProgressOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 100;
  border-radius: 8px;
`;

const SpinnerIcon = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ShowMoreButton = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${props => props.darkMode ? 'rgba(30,30,30,0.8)' : 'rgba(248,249,250,0.8)'};
  border: none;
  padding: 5px;
  text-align: center;
  font-size: 12px;
  color: ${props => props.darkMode ? '#90cdf4' : '#3498db'};
  cursor: pointer;
  
  &:hover {
    background: ${props => props.darkMode ? 'rgba(40,40,40,0.9)' : 'rgba(238,239,240,0.9)'};
  }
`;

// Helper function to check if a URL is a Cloudinary JSON list URL
const isCloudinaryJsonUrl = (url) => {
  return url && typeof url === 'string' && url.includes('/image/list/') && url.endsWith('.json');
};

// Add a BrochureUploader component
const BrochureUploader = ({ 
  title, 
  icon, 
  fieldName, 
  value, 
  handleChange, 
  darkMode, 
  cloudName, 
  uploadPreset, 
  propertyTitle,
  onUploadClick 
}) => {
  const [mode, setMode] = useState('url'); // 'url' or 'upload'
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  
  // Add a ref to the file input for direct manipulation
  const fileInputRef = React.useRef(null);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      // Reset completion status when selecting a new file
      setUploadComplete(false);
    }
  };
  
  // Clear selected file
  const clearFile = () => {
    setSelectedFile(null);
    // Reset the input value directly using the ref
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Upload file to Cloudinary
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }
    
    // Use passed in cloudName and uploadPreset instead of checking them directly
    // This way it will use the same configuration as the images section
    if (!cloudName || !uploadPreset) {
      toast.error("Please configure Cloudinary settings in the Upload section first");
      return;
    }
    
    if (!propertyTitle) {
      toast.error("Please enter a property title first");
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Prepare the folder name (same as images)
      const safeFolderName = sanitizeFolderName(propertyTitle);
      
      // Upload using the same method
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("upload_preset", uploadPreset);
      
      // Add folder parameter
      if (safeFolderName) {
        formData.append("folder", safeFolderName);
      }
      
      // Add a specific public_id based on the brochure type
      if (fieldName === "brochureLink") {
        formData.append("public_id", "brochure");
      } else if (fieldName === "mealsBrochure") {
        formData.append("public_id", "mealbrochure");
      }
      
      // Make direct upload request
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/upload`, {
        method: "POST",
        body: formData
      });
      
      const data = await response.json();
      
      if (data.secure_url) {
        // Set the URL to the form field
        handleChange({
          target: {
            name: fieldName,
            value: data.secure_url
          }
        });
        
        toast.success("File uploaded successfully");
        setUploadComplete(true);
        
        // Reset after 3 seconds
        setTimeout(() => {
          setSelectedFile(null);
          setUploadComplete(false);
        }, 3000);
      } else {
        throw new Error("Upload failed: " + (data.error?.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <div className="mb-3">
      <label className={`form-label d-flex align-items-center gap-2 ${darkMode ? 'text-light' : ''}`}>
        {icon} {title}
      </label>
      
      <div className="d-flex mb-2">
        <div className="btn-group">
          <button
            type="button"
            className={`btn btn-sm ${mode === 'url' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('url')}
          >
            <FaLink className="me-1" /> URL
          </button>
          <button
            type="button"
            className={`btn btn-sm ${mode === 'upload' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => {
              setMode('upload');
              onUploadClick(); // Call the function to reveal Cloudinary config
            }}
            // Disable if Cloudinary isn't configured
            disabled={!cloudName || !uploadPreset}
          >
            <FaUpload className="me-1" /> Upload
          </button>
        </div>
      </div>
      
      {mode === 'url' ? (
        <div className="input-group">
          <span className={`input-group-text ${darkMode ? 'bg-dark border-secondary' : 'bg-light border-end-0'}`}>
            <FaLink />
          </span>
          <input 
            type="url" 
            className={`form-control ${darkMode ? 'border-secondary' : 'border-start-0 shadow-sm'}`}
            name={fieldName}
            value={value || ''}
            onChange={handleChange}
            placeholder={`https://example.com/${fieldName}.pdf`}
          />
        </div>
      ) : (
        <div className={`border rounded p-3 ${darkMode ? 'bg-dark border-secondary' : 'bg-light'}`}>
          {!cloudName || !uploadPreset ? (
            <div className="alert alert-warning mb-0">
              <FaExclamationTriangle className="me-2" />
              Please configure Cloudinary settings in the Upload section above first.
            </div>
          ) : !uploadComplete ? (
            <>
              <div className="mb-3">
                <input 
                  ref={fileInputRef} 
                  type="file" 
                  className="form-control" 
                  accept=".pdf,.doc,.docx" 
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  key={selectedFile ? "has-file" : "no-file"} // Force re-render on clear
                />
                <small className={`d-block mt-1 ${darkMode ? 'text-light' : 'text-muted'}`}>
                  Accepted formats: PDF, Word documents
                </small>
              </div>
              
              {/* File information & upload button - always show upload section */}
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                <div>
                  {selectedFile ? (
                    <>
                      <small className={`fw-bold ${darkMode ? 'text-light' : ''}`}>{selectedFile.name}</small>
                      <small className={`ms-2 ${darkMode ? 'text-light-50' : 'text-muted'}`}>
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </small>
                    </>
                  ) : (
                    <small className={darkMode ? 'text-light-50' : 'text-muted'}>
                      No file selected yet
                    </small>
                  )}
                </div>
                <div className="mt-2 mt-sm-0">
                  {selectedFile && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger me-2"
                      onClick={clearFile}
                      disabled={isUploading}
                    >
                      <FaTrash size={12} className="me-1" /> Clear
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleUpload}
                    disabled={isUploading || !selectedFile}
                  >
                    {isUploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaCloudUploadAlt size={12} className="me-1" /> Upload to Cloudinary
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className={`alert ${darkMode ? 'alert-success bg-success-dark text-light' : 'alert-success'} mb-0`}>
              <div className="d-flex align-items-center">
                <FaCheckCircle size={18} className="me-2" />
                <div>File uploaded successfully! URL has been added to the form.</div>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${darkMode ? 'btn-outline-light' : 'btn-outline-success'} mt-2`}
                onClick={clearFile}
              >
                <FaUpload size={12} className="me-1" /> Upload Another File
              </button>
            </div>
          )}
        </div>
      )}
      
      {value && (
        <div className="mt-2">
          <a href={value} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
            <FaEye className="me-1" /> View {title}
          </a>
        </div>
      )}
    </div>
  );
};

// Add a CloudinaryConfig component for universal Cloudinary settings
const CloudinaryConfig = ({ 
  cloudinaryData, 
  handleCloudinaryChange, 
  formErrors, 
  darkMode,
  propertyTitle
}) => {
  return (
    <div className={`mb-4 p-3 rounded ${darkMode ? 'bg-dark' : 'bg-light'}`}>
      <h6 className={`d-flex align-items-center gap-2 ${darkMode ? 'text-light' : ''}`}>
        <FaCloud /> Cloudinary Configuration
      </h6>
      <p className="small text-muted">
        Enter your Cloudinary credentials once to use them across all upload fields.
      </p>
      
      <div className="row">
        <div className="col-md-6 mb-2">
          <label className={`form-label ${darkMode ? 'text-light' : ''}`}>
            Cloud Name<RequiredMark>*</RequiredMark>
          </label>
          <input
            type="text"
            className={`form-control ${formErrors.cloudName ? 'is-invalid' : ''}`}
            name="cloudName"
            value={cloudinaryData.cloudName}
            onChange={handleCloudinaryChange}
            placeholder="your-cloud-name"
          />
          {formErrors.cloudName && (
            <div className="invalid-feedback">
              Cloud name is required
            </div>
          )}
        </div>
        <div className="col-md-6 mb-2">
          <label className={`form-label ${darkMode ? 'text-light' : ''}`}>
            Upload Preset<RequiredMark>*</RequiredMark>
          </label>
          <div className="input-group">
            <span className="input-group-text">
              <FaCloud />
            </span>
            <input
              type="text"
              className={`form-control ${formErrors.uploadPreset ? 'is-invalid' : ''}`}
              name="uploadPreset"
              value={cloudinaryData.uploadPreset}
              onChange={handleCloudinaryChange}
              placeholder="your-upload-preset"
            />
            {formErrors.uploadPreset && (
              <div className="invalid-feedback">
                Upload preset is required
              </div>
            )}
          </div>
          <small className="text-muted">
            Your Cloudinary unsigned upload preset name
          </small>
        </div>
      </div>
      
      {propertyTitle && (
        <div className={`alert ${darkMode ? 'alert-info bg-info-dark text-light' : 'alert-info'} mt-2 mb-0`}>
          <small>
            <FaFolder className="me-1" />
            All files will be uploaded to folder: <strong>{sanitizeFolderName(propertyTitle)}</strong>
          </small>
        </div>
      )}
    </div>
  );
};

const CoverImageUploader = ({
  value,
  handleChange,
  darkMode,
  cloudName,
  uploadPreset,
  propertyTitle,
  onUploadClick
}) => {
  const [mode, setMode] = useState('url'); // 'url' or 'upload'
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  
  // Add a ref for the file input
  const fileInputRef = React.useRef(null);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadComplete(false);
    }
  };
  
  // Clear selected file
  const clearFile = () => {
    setSelectedFile(null);
    // Reset the input value directly
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  // Upload file to Cloudinary
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }
    
    if (!cloudName || !uploadPreset) {
      toast.error("Please configure Cloudinary settings in the Upload section first");
      return;
    }
    
    if (!propertyTitle) {
      toast.error("Please enter a property title first");
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Prepare the folder name
      const safeFolderName = sanitizeFolderName(propertyTitle);
      
      // Upload the file
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("upload_preset", uploadPreset);
      
      // Add folder parameter
      if (safeFolderName) {
        formData.append("folder", safeFolderName);
      }
      
      // Make direct upload request
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/upload`, {
        method: "POST",
        body: formData
      });
      
      const data = await response.json();
      
      if (data.secure_url) {
        // Set the URL to the form field
        handleChange({
          target: {
            name: "coverImageUrl",
            value: data.secure_url
          }
        });
        
        toast.success("Cover image uploaded successfully");
        setUploadComplete(true);
        
        // Reset after 3 seconds
        setTimeout(() => {
          setSelectedFile(null);
          setUploadComplete(false);
        }, 3000);
      } else {
        throw new Error("Upload failed: " + (data.error?.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <>
      {/* Modified to match Additional Images button placement */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div></div> {/* Empty div for spacing */}
        <TabHeaderButtons className="btn-group">
          <Tippy content="Add Image URL">
            <button
              type="button"
              className={`btn btn-sm ${mode === 'url' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('url')}
            >
              <FaLink className="me-1" /> URL
            </button>
          </Tippy>
          <Tippy content="Upload to Cloudinary">
            <button
              type="button"
              className={`btn btn-sm ${mode === 'upload' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setMode('upload');
                onUploadClick(); // Call the function to reveal Cloudinary config
              }}
              disabled={!cloudName || !uploadPreset}
            >
              <FaCloud className="me-1" /> Upload
            </button>
          </Tippy>
        </TabHeaderButtons>
      </div>
      
      {mode === 'url' ? (
        <div className="input-group mb-3">
          <span className={`input-group-text ${darkMode ? 'bg-dark border-secondary' : 'bg-light border-end-0'}`}>
            <FaLink />
          </span>
          <input 
            type="url" 
            className={`form-control ${darkMode ? 'border-secondary' : 'border-start-0 shadow-sm'}`}
            name="coverImageUrl"
            value={value || ''}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
          />
        </div>
      ) : (
        <div className={`border rounded p-3 ${darkMode ? 'bg-dark border-secondary' : 'bg-light'} mb-3`}>
          {!cloudName || !uploadPreset ? (
            <div className="alert alert-warning mb-0">
              <FaExclamationTriangle className="me-2" />
              Please configure Cloudinary settings in the Upload section first.
            </div>
          ) : !uploadComplete ? (
            <>
              <div className="mb-3">
                <input 
                  ref={fileInputRef}
                  type="file" 
                  className="form-control" 
                  accept="image/*" 
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  key={selectedFile ? "has-file" : "no-file"} // Force re-render on clear
                />
                <small className={`d-block mt-1 ${darkMode ? 'text-light' : 'text-muted'}`}>
                  Select an image file to upload as the cover image
                </small>
              </div>
              
              {/* Improved mobile alignment for file info and buttons */}
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                <div>
                  {selectedFile ? (
                    <>
                      <small className={`fw-bold ${darkMode ? 'text-light' : ''}`}>{selectedFile.name}</small>
                      <small className={`ms-2 ${darkMode ? 'text-light-50' : 'text-muted'}`}>
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </small>
                    </>
                  ) : (
                    <small className={darkMode ? 'text-light-50' : 'text-muted'}>
                      No file selected yet
                    </small>
                  )}
                </div>
                <div className="mt-2 mt-sm-0">
                  {selectedFile && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger me-2"
                      onClick={clearFile}
                      disabled={isUploading}
                    >
                      <FaTrash size={12} className="me-1" /> Clear
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleUpload}
                    disabled={isUploading || !selectedFile}
                  >
                    {isUploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaCloudUploadAlt size={12} className="me-1" /> Upload to Cloudinary
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className={`alert ${darkMode ? 'alert-success bg-success-dark text-light' : 'alert-success'} mb-0`}>
              <div className="d-flex align-items-center">
                <FaCheckCircle size={18} className="me-2" />
                <div>Cover image uploaded successfully!</div>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${darkMode ? 'btn-outline-light' : 'btn-outline-success'} mt-2`}
                onClick={clearFile}
              >
                <FaUpload size={12} className="me-1" /> Upload Another Image
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const ImagesTab = ({ 
  formData, 
  handleChange, 
  tempArrayFields,
  setTempArrayFields,
  handleArrayItemChange,
  handleAddArrayItem,
  handleRemoveArrayItem,
  handleDragEnd,
  darkMode
}) => {
  // State to control displaying the Cloudinary config section
  const [showCloudinaryConfig, setShowCloudinaryConfig] = useState(false);
  
  // Load Cloudinary settings from localStorage if available
  const loadCloudinarySettings = () => {
    try {
      const savedSettings = localStorage.getItem('cloudinarySettings');
      if (savedSettings) {
        return JSON.parse(savedSettings);
      }
    } catch (error) {
      console.error("Error loading Cloudinary settings:", error);
    }
    
    // Set default Cloudinary credentials
    return {
      cloudName: 'dccu1qdqr',
      uploadPreset: 'Lsq stays',
      format: 'auto'
    };
  };
  
  // Function to show Cloudinary config when clicking any upload button
  // Modified to add a source parameter to control behavior
  const revealCloudinaryConfig = (source = 'images') => {
    setShowCloudinaryConfig(true);
    
    // Only switch the image upload mode if called from the images section
    if (source === 'images') {
      setUploadState({
        ...uploadState,
        mode: 'cloudinary'
      });
    }
  };
  
  // Cloudinary state with required preset name instead of folder name
  const [cloudinaryData, setCloudinaryData] = useState(loadCloudinarySettings());
  
  // State for merged upload functionality
  const [uploadState, setUploadState] = useState({
    mode: 'url', // 'url' or 'cloudinary'
    isUploading: false,
    selectedFiles: [],
    showAllFiles: false,
    uploadComplete: false,
    uploadedCount: 0
  });
  
  // Validation states
  const [formErrors, setFormErrors] = useState({
    cloudName: false,
    uploadPreset: false
  });
  
  // Handle Cloudinary input changes
  const handleCloudinaryChange = (e) => {
    const { name, value } = e.target;
    
    // Update the cloudinary data
    const updatedData = {
      ...cloudinaryData,
      [name]: value
    };
    
    setCloudinaryData(updatedData);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('cloudinarySettings', JSON.stringify(updatedData));
    } catch (error) {
      console.error("Error saving Cloudinary settings:", error);
    }
    
    // Clear error state for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: false
      });
    }
  };
  
  // Handle file selection (without uploading yet)
  const onDrop = (acceptedFiles) => {
    setUploadState({
      ...uploadState,
      selectedFiles: [...uploadState.selectedFiles, ...acceptedFiles]
    });
  };
  
  // Remove a file from the selection
  const removeSelectedFile = (index) => {
    const updatedFiles = [...uploadState.selectedFiles];
    updatedFiles.splice(index, 1);
    setUploadState({
      ...uploadState,
      selectedFiles: updatedFiles
    });
  };
  
  // Clear all selected files
  const clearSelectedFiles = () => {
    setUploadState({
      ...uploadState,
      selectedFiles: []
    });
  };
  
  // Setup react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    onDrop
  });
  
  // Validate inputs before upload
  const validateCloudinaryInputs = () => {
    const errors = {
      cloudName: !cloudinaryData.cloudName,
      uploadPreset: !cloudinaryData.uploadPreset
    };
    
    setFormErrors(errors);
    return !Object.values(errors).some(isError => isError);
  };
  
  // Upload files to Cloudinary (real implementation)
  const handleUploadToCloudinary = async () => {
    if (!uploadState.selectedFiles.length) {
      toast.error("Please select files to upload");
      return;
    }
    
    // Check if Cloudinary is configured
    if (!cloudinaryData.cloudName || !cloudinaryData.uploadPreset) {
      toast.error("Please configure Cloudinary settings first");
      return;
    }
    
    // Check if property title exists to use as folder name
    if (!formData.propertyTitle) {
      toast.error("Please enter a property title first (it will be used as the folder name)");
      return;
    }
    
    // Start upload
    setUploadState({
      ...uploadState,
      isUploading: true,
      uploadComplete: false
    });
    
    try {
      // Sanitize property title to use as folder name
      const safeFolderName = sanitizeFolderName(formData.propertyTitle);
      
      // Get existing URLs to check for naming conflicts
      const existingUrls = [...tempArrayFields.additionalImagesUrl];
      
      // Use the real Cloudinary upload function to upload multiple files
      const result = await uploadMultipleToCloudinary(
        uploadState.selectedFiles,
        cloudinaryData.cloudName,
        cloudinaryData.uploadPreset,
        safeFolderName,
        true, // Enable sequential naming
        existingUrls // Pass existing URLs to avoid naming conflicts
      );
      
      console.log("Upload result:", result); // Debug log
      
      if (result.success || result.urls.length > 0) {
        // Create a new array with existing URLs plus the new ones
        const newUrls = [...tempArrayFields.additionalImagesUrl, ...result.urls];
        
        console.log("New URLs array:", newUrls); // Debug log
        
        // Update all URLs at once
        setTempArrayFields({
          ...tempArrayFields,
          additionalImagesUrl: newUrls
        });
        
        toast.success(`${result.uploadedCount} images uploaded to Cloudinary folder`);
        
        // Set upload as complete and track count
        setUploadState({
          ...uploadState,
          isUploading: false,
          uploadComplete: true,
          uploadedCount: result.uploadedCount
        });
        
        // After 3 seconds, clear the file selection to reset the form
        setTimeout(() => {
          setUploadState({
            ...uploadState,
            selectedFiles: [],
            isUploading: false,
            uploadComplete: false,
            uploadedCount: 0
          });
        }, 3000);
      } else {
        toast.error("Failed to upload images. Please check your Cloudinary settings.");
        
        setUploadState({
          ...uploadState,
          isUploading: false,
          uploadComplete: false
        });
        
        // Show detailed errors
        if (result.errors.length > 0) {
          console.error("Upload errors:", result.errors);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Upload failed: ${error.message}`);
      
      setUploadState({
        ...uploadState,
        isUploading: false,
        uploadComplete: false
      });
    }
  };
  
  // Toggle view all files
  const toggleShowAllFiles = () => {
    setUploadState({
      ...uploadState,
      showAllFiles: !uploadState.showAllFiles
    });
  };
  
  // Helper for rendering file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {/* Universal Cloudinary Configuration - Only show when an upload button is clicked */}
      <AnimatePresence>
        {showCloudinaryConfig && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CloudinaryConfig 
              cloudinaryData={cloudinaryData}
              handleCloudinaryChange={handleCloudinaryChange}
              formErrors={formErrors}
              darkMode={darkMode}
              propertyTitle={formData.propertyTitle}
            />
          </motion.div>
        )}
      </AnimatePresence>
    
      {/* Cover Image URL - Updated with styled header similar to additional images */}
      <ArrayFieldContainer darkMode={darkMode}>
        <ArrayHeader>
          <ArrayLabel darkMode={darkMode}>
            <FaImage /> Cover Image
          </ArrayLabel>
        </ArrayHeader>
        
        {/* Cover Image Uploader */}
        <CoverImageUploader
          value={formData.coverImageUrl}
          handleChange={handleChange}
          darkMode={darkMode}
          cloudName={cloudinaryData.cloudName}
          uploadPreset={cloudinaryData.uploadPreset}
          propertyTitle={formData.propertyTitle}
          onUploadClick={() => revealCloudinaryConfig('cover')}
        />
        
        {formData.coverImageUrl && (
          <div className="mt-2 position-relative">
            <SmallImagePreview
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <PreviewImage 
                src={formData.coverImageUrl} 
                alt="Cover Preview"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                }}
              />
              <ImageActions>
                <Tippy content="View full size">
                  <ActionButton as="a" href={formData.coverImageUrl} target="_blank" rel="noopener noreferrer" type="button">
                    <FaEye size={14} />
                  </ActionButton>
                </Tippy>
              </ImageActions>
            </SmallImagePreview>
          </div>
        )}
      </ArrayFieldContainer>
      
      {/* Additional Images with merged Cloudinary upload */}
      <ArrayFieldContainer darkMode={darkMode}>
        <ArrayHeader>
          <ArrayLabel darkMode={darkMode}>
            <FaImage /> Additional Images
          </ArrayLabel>
          <TabHeaderButtons className="btn-group">
            <Tippy content="Add Image URL">
              <button
                type="button"
                className={`btn btn-sm ${uploadState.mode === 'url' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setUploadState({...uploadState, mode: 'url'})}
              >
                <FaLink className="me-1" /> URL
              </button>
            </Tippy>
            <Tippy content="Upload to Cloudinary">
              <button
                type="button"
                className={`btn btn-sm ${uploadState.mode === 'cloudinary' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => {
                  // Pass 'images' as the source to indicate this is from images section
                  revealCloudinaryConfig('images');
                }}
              >
                <FaCloud className="me-1" /> Upload
              </button>
            </Tippy>
          </TabHeaderButtons>
        </ArrayHeader>
        <AnimatePresence mode="wait">
          {uploadState.mode === 'cloudinary' && (
            <motion.div
              key="cloudinary-upload"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CloudinaryUploadContainer darkMode={darkMode}>
                <p className={`mb-3 ${darkMode ? 'text-light' : ''}`}>
                  <FaCloud className="me-2" />
                  Upload images to Cloudinary
                </p>
                
                {/* Dropzone */}
                <div className="position-relative">
                  <DropzoneContainer
                    {...getRootProps()}
                    isDragActive={isDragActive}
                    darkMode={darkMode}
                  >
                    <input {...getInputProps()} />
                    <DropzoneIcon isDragActive={isDragActive}>
                      <FaUpload />
                    </DropzoneIcon>
                    {isDragActive ? (
                      <DropzoneText isDragActive={isDragActive}>Drop the files here ...</DropzoneText>
                    ) : (
                      <DropzoneText>
                        Drag & drop image files here, or click to select files
                      </DropzoneText>
                    )}
                  </DropzoneContainer>
                  
                  {/* Upload progress overlay */}
                  {uploadState.isUploading && (
                    <UploadProgressOverlay>
                      <SpinnerIcon size={30} />
                      <p>Uploading to Cloudinary...</p>
                    </UploadProgressOverlay>
                  )}
                </div>
                
                {/* File preview - Modified to show success message */}
                {uploadState.selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small>
                        {uploadState.uploadComplete ? (
                          <span className="text-success">
                            <FaCheck className="me-1" /> 
                            Successfully uploaded <strong>{uploadState.uploadedCount}</strong> images
                          </span>
                        ) : (
                          <span>
                            <strong>{uploadState.selectedFiles.length}</strong> files selected
                          </span>
                        )}
                      </small>
                      <div>
                        {!uploadState.uploadComplete && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger me-2"
                              onClick={clearSelectedFiles}
                              disabled={uploadState.isUploading}
                            >
                              <FaTrash size={12} className="me-1" /> Clear
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleUploadToCloudinary}
                              disabled={uploadState.isUploading}
                            >
                              <FaCloud size={12} className="me-1" /> Upload to Cloudinary
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Show preview only if not upload complete */}
                    {!uploadState.uploadComplete && (
                      <div className="position-relative">
                        <UploadPreview 
                          isExpanded={uploadState.showAllFiles} 
                          darkMode={darkMode}
                        >
                          {uploadState.selectedFiles.map((file, idx) => (
                            <PreviewItem key={idx} darkMode={darkMode}>
                              <img 
                                src={URL.createObjectURL(file)} 
                                alt={`Preview ${idx}`}
                              />
                              <span>{file.name} ({formatFileSize(file.size)})</span>
                              {!uploadState.isUploading && (
                                <div 
                                  style={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    background: 'rgba(0,0,0,0.5)',
                                    borderRadius: '50%',
                                    width: 18,
                                    height: 18,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSelectedFile(idx);
                                  }}
                                >
                                  <FaTrash size={10} color="white" />
                                </div>
                              )}
                            </PreviewItem>
                          ))}
                        </UploadPreview>
                        
                        {uploadState.selectedFiles.length > 6 && !uploadState.showAllFiles && (
                          <ShowMoreButton
                            onClick={toggleShowAllFiles}
                            darkMode={darkMode}
                          >
                            Show {uploadState.selectedFiles.length - 6} more files...
                          </ShowMoreButton>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show success message after upload */}
                {uploadState.uploadComplete && (
                  <div className={`alert ${darkMode ? 'alert-success bg-success-dark text-light' : 'alert-success'}`}>
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <FaCheckCircle size={24} />
                      </div>
                      <div>
                        <strong>Upload successful!</strong> {uploadState.uploadedCount} images uploaded to Cloudinary folder.
                        <p className="mb-0">Individual image URLs have been added to the property gallery.</p>
                      </div>
                    </div>
                  </div>
                )}
              </CloudinaryUploadContainer>
            </motion.div>
          )}
          {uploadState.mode === 'url' && (
            <motion.div
              key="url"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'additionalImagesUrl')}>
                <Droppable droppableId="droppable-additionalImagesUrl">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      <AnimatePresence>
                        {tempArrayFields.additionalImagesUrl.map((item, index) => (
                          <Draggable key={`additionalImagesUrl-${index}`} draggableId={`additionalImagesUrl-${index}`} index={index}>
                            {(provided) => (
                              <ArrayItemContainer
                                {...provided.draggableProps}
                                ref={provided.innerRef}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                transition={{ duration: 0.2 }}
                                darkMode={darkMode}
                              >
                                <DragHandle {...provided.dragHandleProps}>
                                  <FaGripVertical />
                                </DragHandle>
                                <ArrayItemInput
                                  type="url"
                                  value={item}
                                  onChange={(e) => handleArrayItemChange('additionalImagesUrl', index, e.target.value)}
                                  placeholder="https://example.com/image.jpg"
                                  darkMode={darkMode}
                                  onKeyDown={preventEnterSubmit}
                                />
                                {item && (
                                  <div className="d-flex gap-1 flex-shrink-0">
                                    <Tippy content="View image">
                                      <ActionButton 
                                        as="a" 
                                        href={item} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        type="button"
                                      >
                                        <FaEye size={12} />
                                      </ActionButton>
                                    </Tippy>
                                    <Tippy content="Remove image">
                                      <ActionButton 
                                        danger 
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleRemoveArrayItem('additionalImagesUrl', index);
                                          return false;
                                        }}
                                      >
                                        <FaTrash size={12} />
                                      </ActionButton>
                                    </Tippy>
                                  </div>
                                )}
                              </ArrayItemContainer>
                            )}
                          </Draggable>
                        ))}
                      </AnimatePresence>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <AddButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleAddArrayItem('additionalImagesUrl');
                }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                darkMode={darkMode}
              >
                <FaPlus size={14} /> Add Image URL
              </AddButton>
            </motion.div>
          )}
        </AnimatePresence>
      </ArrayFieldContainer>
      
      {/* Image Previews - Modified to skip JSON list URLs */}
      {tempArrayFields.additionalImagesUrl.length > 0 && (
        <ImagePreviewsContainer>
          {tempArrayFields.additionalImagesUrl.map((img, idx) => (
            img && !isCloudinaryJsonUrl(img) && (
              <ImagePreview key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
              >
                <PreviewImage 
                  src={img} 
                  alt={`Additional ${idx+1}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                  }}
                />
                <ImageActions>
                  <Tippy content="View full size">
                    <ActionButton 
                      as="a" 
                      href={img} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      type="button"
                    >
                      <FaEye size={14} />
                    </ActionButton>
                  </Tippy>
                  <Tippy content="Remove">
                    <ActionButton 
                      danger 
                      type="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveArrayItem('additionalImagesUrl', idx);
                        return false;
                      }}
                    >
                      <FaTrash size={14} />
                    </ActionButton>
                  </Tippy>
                </ImageActions>
              </ImagePreview>
            )
          ))}
          
          {/* Add special display for JSON list URLs */}
          {tempArrayFields.additionalImagesUrl.some(url => isCloudinaryJsonUrl(url)) && (
            <div className={`w-100 p-3 mt-2 mb-3 rounded ${darkMode ? 'bg-dark text-light' : 'bg-light'}`}>
              <div className="d-flex align-items-center">
                <FaCloud className="me-2" size={18} />
                <h6 className="mb-0">Cloudinary Image Collection</h6>
              </div>
              <p className="small text-muted mt-2 mb-0">
                This property uses a Cloudinary image collection. The images are stored in a Cloudinary folder and will be loaded dynamically.
              </p>
            </div>
          )}
        </ImagePreviewsContainer>
      )}
      
      {/* Replace brochure links section with the new BrochureUploader component */}
      <div className="row mt-4">
        <div className="col-md-6">
          <BrochureUploader
            title="Brochure Link"
            icon={<FaClipboardList />}
            fieldName="brochureLink"
            value={formData.brochureLink}
            handleChange={handleChange}
            darkMode={darkMode}
            cloudName={cloudinaryData.cloudName}
            uploadPreset={cloudinaryData.uploadPreset}
            propertyTitle={formData.propertyTitle}
            onUploadClick={() => revealCloudinaryConfig('brochure')}
          />
        </div>
        <div className="col-md-6">
          <BrochureUploader
            title="Meals Brochure"
            icon={<FaClipboardList />}
            fieldName="mealsBrochure"
            value={formData.mealsBrochure}
            handleChange={handleChange}
            darkMode={darkMode}
            cloudName={cloudinaryData.cloudName}
            uploadPreset={cloudinaryData.uploadPreset}
            propertyTitle={formData.propertyTitle}
            onUploadClick={() => revealCloudinaryConfig('brochure')}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default ImagesTab;